import { LegalPageLayout } from "@/components/legal/LegalPageLayout";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const CGV = () => {
  usePageMeta({
    title: "Conditions Générales de Vente — B8ild",
    description: "Conditions générales de vente de B8ild, logiciel de gestion de chantier BTP. Tarifs, modalités de paiement, essai gratuit et politique d'abonnement.",
    url: "https://b8ild.fr/cgv",
  });

  return (
    <LegalPageLayout
      title="Conditions Générales de Vente"
      subtitle="Dernière mise à jour : Février 2026"
    >
      <h2>Article 1 - Objet</h2>
      <p>
        Les présentes Conditions Générales de Vente (ci-après "CGV") régissent les relations contractuelles
        entre <strong>B8ild LLC</strong> (ci-après "le Prestataire") et toute personne physique ou morale
        souscrivant un abonnement à la plateforme B8ild accessible à l'adresse{" "}
        <a href="https://b8ild.fr" target="_blank" rel="noopener noreferrer">https://b8ild.fr</a>{" "}
        (ci-après "le Client").
      </p>
      <p>
        Toute souscription à un abonnement implique l'acceptation sans réserve des présentes CGV,
        qui prévalent sur tout autre document.
      </p>

      <h2>Article 2 - Description des services</h2>
      <p>
        B8ild est une plateforme SaaS (Software as a Service) de pilotage de rentabilité de chantiers
        destinée aux professionnels du BTP. Les services comprennent :
      </p>
      <ul>
        <li>Suivi de la rentabilité des chantiers en temps réel</li>
        <li>Calcul automatique des marges et du "Jour Critique"</li>
        <li>Gestion des équipes et affectations</li>
        <li>Import et traitement des factures par OCR (reconnaissance optique)</li>
        <li>Analyse financière par intelligence artificielle</li>
        <li>Génération de rapports et bilans de chantier</li>
        <li>Alertes et notifications personnalisées</li>
      </ul>
      <p>
        Les fonctionnalités disponibles dépendent du plan d'abonnement souscrit.
        Le détail des fonctionnalités par plan est consultable sur la{" "}
        <Link to="/pricing">page Tarifs</Link>.
      </p>

      <h2>Article 3 - Tarifs</h2>
      <h3>3.1. Plans d'abonnement</h3>
      <p>B8ild propose les plans suivants (tarifs mensuels HT) :</p>
      <table>
        <thead>
          <tr>
            <th>Plan</th>
            <th>Prix mensuel HT</th>
            <th>Utilisateurs inclus</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Starter</td>
            <td>49 €</td>
            <td>2</td>
          </tr>
          <tr>
            <td>Pro</td>
            <td>99 €</td>
            <td>5</td>
          </tr>
          <tr>
            <td>Entreprise</td>
            <td>149 €</td>
            <td>15</td>
          </tr>
        </tbody>
      </table>

      <h3>3.2. TVA</h3>
      <p>
        Les prix sont indiqués en euros hors taxes (HT). La TVA applicable en vigueur sera ajoutée
        au montant hors taxes lors de la facturation.
      </p>

      <h3>3.3. Modification des tarifs</h3>
      <p>
        Le Prestataire se réserve le droit de modifier ses tarifs à tout moment. Toute modification
        sera notifiée au Client par email au moins 30 jours avant son entrée en vigueur. Le Client
        pourra résilier son abonnement avant l'application du nouveau tarif.
      </p>

      <h2>Article 4 - Essai gratuit</h2>
      <p>
        Un essai gratuit de 14 jours est proposé pour chaque nouveau compte. Pendant cette période,
        le Client bénéficie de l'ensemble des fonctionnalités du plan souscrit.
      </p>
      <p>
        Une carte bancaire est requise pour activer l'essai gratuit. Aucun prélèvement n'est effectué
        pendant la période d'essai. À l'issue des 14 jours, l'abonnement est automatiquement activé
        sauf résiliation préalable par le Client.
      </p>

      <h2>Article 5 - Commande et souscription</h2>
      <h3>5.1.</h3>
      <p>
        La souscription s'effectue en ligne sur la Plateforme. Le Client choisit son plan d'abonnement,
        renseigne ses informations et procède au paiement.
      </p>
      <h3>5.2.</h3>
      <p>
        La souscription est effective dès la confirmation du paiement (ou dès l'activation de l'essai
        gratuit). Un email de confirmation est envoyé au Client.
      </p>
      <h3>5.3.</h3>
      <p>
        Le Client peut changer de plan à tout moment depuis son espace personnel. Le changement
        prend effet immédiatement, avec un prorata calculé automatiquement.
      </p>

      <h2>Article 6 - Modalités de paiement</h2>
      <h3>6.1.</h3>
      <p>
        Le paiement s'effectue par carte bancaire via le prestataire de paiement sécurisé{" "}
        <strong>Stripe</strong>. Les données bancaires sont traitées directement par Stripe
        et ne sont jamais stockées sur les serveurs de B8ild.
      </p>
      <h3>6.2.</h3>
      <p>
        La facturation est mensuelle. Le prélèvement est effectué automatiquement à chaque
        date anniversaire de la souscription.
      </p>
      <h3>6.3.</h3>
      <p>
        En cas d'échec de paiement, le Client est notifié par email. Si le paiement n'est pas
        régularisé dans un délai de 7 jours, l'accès aux services pourra être suspendu.
      </p>
      <h3>6.4.</h3>
      <p>
        Une facture est mise à disposition du Client dans son espace personnel après chaque paiement.
      </p>

      <h2>Article 7 - Durée et renouvellement</h2>
      <h3>7.1.</h3>
      <p>
        L'abonnement est souscrit pour une durée indéterminée avec facturation mensuelle.
      </p>
      <h3>7.2.</h3>
      <p>
        L'abonnement se renouvelle automatiquement chaque mois, sauf résiliation par le Client.
      </p>

      <h2>Article 8 - Droit de rétractation</h2>
      <p>
        Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation
        ne s'applique pas aux contrats de fourniture de contenu numérique fourni sur un support
        immatériel dont l'exécution a commencé avec l'accord du consommateur.
      </p>
      <p>
        B8ild étant un service SaaS à destination des professionnels (B2B), le droit de rétractation
        prévu aux articles L221-18 et suivants du Code de la consommation ne s'applique pas.
      </p>
      <p>
        Néanmoins, l'essai gratuit de 14 jours permet au Client de tester le service sans engagement
        financier.
      </p>

      <h2>Article 9 - Résiliation</h2>
      <h3>9.1. Résiliation par le Client</h3>
      <p>
        Le Client peut résilier son abonnement à tout moment depuis son espace personnel
        (Réglages &gt; Abonnement) ou en contactant{" "}
        <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a>.
      </p>
      <h3>9.2.</h3>
      <p>
        La résiliation prend effet à la fin de la période de facturation en cours. Le Client
        conserve l'accès au service jusqu'à cette date.
      </p>
      <h3>9.3. Conservation des données</h3>
      <p>
        Après résiliation, le Client conserve l'accès à ses données pendant 30 jours pour les
        exporter. Passé ce délai, les données seront définitivement supprimées conformément
        à notre <Link to="/confidentialite">Politique de Confidentialité</Link>.
      </p>
      <h3>9.4. Résiliation par le Prestataire</h3>
      <p>
        Le Prestataire se réserve le droit de suspendre ou résilier l'accès d'un Client en cas
        de violation des <Link to="/cgu">Conditions Générales d'Utilisation</Link>, de non-paiement,
        ou d'utilisation abusive du service, sans préavis ni indemnité.
      </p>

      <h2>Article 10 - Mise à disposition du service</h2>
      <h3>10.1.</h3>
      <p>
        Le service est accessible en ligne via un navigateur web compatible, 24h/24 et 7j/7,
        sous réserve des opérations de maintenance.
      </p>
      <h3>10.2.</h3>
      <p>
        Le Prestataire s'engage à informer le Client de toute interruption programmée pour
        maintenance dans un délai raisonnable.
      </p>
      <h3>10.3.</h3>
      <p>
        L'accès au service nécessite une connexion internet dont le Client est seul responsable.
      </p>

      <h2>Article 11 - Garantie et responsabilité</h2>
      <h3>11.1.</h3>
      <p>
        Le Prestataire s'engage à fournir un service conforme à la description des plans d'abonnement
        et à mettre en œuvre les moyens nécessaires pour assurer la continuité et la qualité du service.
      </p>
      <h3>11.2.</h3>
      <p>Le Prestataire ne saurait être tenu responsable :</p>
      <ul>
        <li>Des dommages indirects (perte de chiffre d'affaires, perte de données, manque à gagner)</li>
        <li>Des décisions prises par le Client sur la base des informations fournies par la Plateforme</li>
        <li>Des interruptions dues à des cas de force majeure ou à des tiers (hébergeur, opérateur télécom)</li>
      </ul>
      <h3>11.3.</h3>
      <p>
        Les analyses, calculs et recommandations fournis par B8ild (y compris par intelligence artificielle)
        sont des outils d'aide à la décision. Le Client reste seul responsable de ses décisions de gestion.
      </p>
      <h3>11.4.</h3>
      <p>
        En tout état de cause, la responsabilité du Prestataire est limitée au montant total des sommes
        effectivement versées par le Client au cours des 12 derniers mois précédant le fait générateur.
      </p>

      <h2>Article 12 - Propriété des données</h2>
      <h3>12.1.</h3>
      <p>
        Le Client conserve l'entière propriété de ses données (informations chantiers, factures,
        données financières, données d'équipe). Le Prestataire n'acquiert aucun droit sur ces données.
      </p>
      <h3>12.2.</h3>
      <p>
        Le Client peut exporter ses données à tout moment depuis son espace personnel, conformément
        au droit à la portabilité (article 20 du RGPD).
      </p>

      <h2>Article 13 - Force majeure</h2>
      <p>
        Le Prestataire ne pourra être tenu responsable de l'inexécution de ses obligations en cas
        de force majeure, notamment : pannes de réseau, catastrophes naturelles, actes de terrorisme,
        grèves, pandémies, ou tout événement indépendant de sa volonté au sens de l'article 1218
        du Code civil.
      </p>

      <h2>Article 14 - Protection des données personnelles</h2>
      <p>
        Le traitement des données personnelles est régi par notre{" "}
        <Link to="/confidentialite">Politique de Confidentialité</Link>, conforme au Règlement Général
        sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
      </p>

      <h2>Article 15 - Hébergement</h2>
      <p>Le site est hébergé par :</p>
      <p>
        <strong>Vercel Inc.</strong> — 340 S Lemon Ave #4133, Walnut, CA 91789, USA
      </p>
      <p>Les données sont hébergées par :</p>
      <p>
        <strong>Supabase Inc.</strong> — 970 Toa Payoh North #07-04, Singapore 318992
      </p>

      <h2>Article 16 - Loi applicable et litiges</h2>
      <h3>16.1.</h3>
      <p>Les présentes CGV sont soumises au droit français.</p>
      <h3>16.2.</h3>
      <p>
        En cas de litige, les parties s'engagent à rechercher une solution amiable avant toute
        action judiciaire. La réclamation doit être adressée par email à{" "}
        <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a>.
      </p>
      <h3>16.3.</h3>
      <p>
        Conformément aux articles L611-1 et suivants du Code de la consommation, le Client peut
        recourir gratuitement à un médiateur de la consommation en vue de la résolution amiable
        du litige.
      </p>
      <h3>16.4.</h3>
      <p>
        À défaut d'accord amiable dans un délai de 30 jours, les tribunaux compétents du ressort
        du siège social du Prestataire seront seuls compétents.
      </p>

      <h2>Article 17 - Contact</h2>
      <p>Pour toute question relative aux présentes CGV :</p>
      <ul>
        <li>Email : <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a></li>
        <li>Page contact : <Link to="/contact">https://b8ild.fr/contact</Link></li>
      </ul>
    </LegalPageLayout>
  );
};

export default CGV;
